import React, { useEffect, useState } from "react";
import {
  Container,
  Row,
  Col,
  Nav,
  Card,
  Table,
  Badge,
  Button,
  InputGroup,
  Form,
  Modal,
} from "react-bootstrap";
import {
  BsJournalCheck,
  BsEnvelope,
  BsTelephoneOutbound,
  BsSearch,
  BsStars,
  BsEye,
} from "react-icons/bs";
import HelperService from "../../../Services/HelperService";
import RmAiEmailModal from "../Modals/RmAiEmailModal";
import RmLogActivityModal from "../Modals/RmLogActivityModal";

const RmInteractionLogs = () => {
  const [activeTab, setActiveTab] = useState<"MAILS" | "CALLS">("MAILS");
  const [keyword, setKeyword] = useState("");

  const [mailLogs, setMailLogs] = useState<any[]>([]);
  const [callLogs, setCallLogs] = useState<any[]>([]);

  // View modal state
  const [selectedItem, setSelectedItem] = useState<any>(null);
  const [showViewModal, setShowViewModal] = useState(false);

  // Action modals
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [showLogModal, setShowLogModal] = useState(false);

  useEffect(() => {
    loadLogs();
    window.addEventListener("rm_logs_updated", loadLogs);
    return () => window.removeEventListener("rm_logs_updated", loadLogs);
  }, []);

  const loadLogs = () => {
    const storedMails = JSON.parse(localStorage.getItem("rm_mail_logs") || "[]");
    const storedCalls = JSON.parse(localStorage.getItem("rm_call_logs") || "[]");

    setMailLogs(storedMails);
    setCallLogs(storedCalls);
  };

  const getFilteredMails = () => {
    if (!keyword) return mailLogs;
    return mailLogs.filter(
      (m) =>
        m.recipient_name?.toLowerCase().includes(keyword.toLowerCase()) ||
        m.recipient_email?.toLowerCase().includes(keyword.toLowerCase()) ||
        m.subject?.toLowerCase().includes(keyword.toLowerCase())
    );
  };

  const getFilteredCalls = () => {
    if (!keyword) return callLogs;
    return callLogs.filter(
      (c) =>
        c.contact_name?.toLowerCase().includes(keyword.toLowerCase()) ||
        c.contact_email?.toLowerCase().includes(keyword.toLowerCase()) ||
        c.notes?.toLowerCase().includes(keyword.toLowerCase())
    );
  };

  return (
    <div className="page-content pb-5" style={{ minHeight: "calc(100vh - 70px)", background: "#f8f9fa" }}>
      <Container fluid className="px-4 pt-3">
        {/* Header Title */}
        <div className="d-flex flex-wrap align-items-center justify-content-between mb-4">
          <div>
            <h4 className="fw-bold text-dark mb-1 d-flex align-items-center gap-2">
              <BsJournalCheck className="text-info" /> Relation Manager Activity & Communication Logs
            </h4>
            <p className="text-muted font-13 mb-0">
              Audit trail for emails sent (AI drafted) and phone calls logged.
            </p>
          </div>
          <div className="d-flex gap-2">
            <Button
              variant="outline-primary"
              className="rounded-pill font-13 fw-semibold d-flex align-items-center gap-1"
              onClick={() => setShowEmailModal(true)}
            >
              <BsStars className="text-warning" /> AI Email
            </Button>
            <Button
              variant="primary"
              className="rounded-pill font-13 fw-semibold d-flex align-items-center gap-1"
              onClick={() => setShowLogModal(true)}
            >
              <BsTelephoneOutbound /> Log Call
            </Button>
          </div>
        </div>

        {/* Tab & Search */}
        <Row className="g-3 align-items-center mb-4">
          <Col lg={7}>
            <Nav variant="pills" className="bg-white p-1.5 rounded-pill border shadow-xs d-inline-flex">
              <Nav.Item>
                <Nav.Link
                  active={activeTab === "MAILS"}
                  onClick={() => setActiveTab("MAILS")}
                  className={`rounded-pill font-13 fw-semibold px-3 py-1.5 d-flex align-items-center gap-1.5 ${activeTab === "MAILS" ? "bg-primary text-white" : "text-dark"
                    }`}
                >
                  <BsEnvelope /> Mails Done ({mailLogs.length})
                </Nav.Link>
              </Nav.Item>

              <Nav.Item>
                <Nav.Link
                  active={activeTab === "CALLS"}
                  onClick={() => setActiveTab("CALLS")}
                  className={`rounded-pill font-13 fw-semibold px-3 py-1.5 d-flex align-items-center gap-1.5 ${activeTab === "CALLS" ? "bg-success text-white" : "text-dark"
                    }`}
                >
                  <BsTelephoneOutbound /> Calls Done ({callLogs.length})
                </Nav.Link>
              </Nav.Item>
            </Nav>
          </Col>

          <Col lg={5}>
            <InputGroup className="shadow-xs rounded-pill overflow-hidden border">
              <Form.Control
                placeholder="Search logs by recipient, subject, notes..."
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
              />
              <InputGroup.Text className="bg-white text-muted">
                <BsSearch />
              </InputGroup.Text>
            </InputGroup>
          </Col>
        </Row>

        {/* Tab Contents */}
        <Card className="border-0 shadow-sm rounded-4 overflow-hidden">
          {activeTab === "MAILS" && (
            <div className="table-responsive">
              <Table hover className="align-middle mb-0 table-style-1">
                <thead className="bg-light">
                  <tr>
                    <th className="py-3 px-3">#</th>
                    <th className="py-3">Recipient</th>
                    <th className="py-3">Subject Line</th>
                    <th className="py-3">AI Drafted</th>
                    <th className="py-3">Date & Time</th>
                    <th className="py-3 text-center">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {getFilteredMails().length > 0 ? (
                    getFilteredMails().map((item: any, idx: number) => (
                      <tr key={idx}>
                        <td className="px-3 font-13 fw-semibold">{idx + 1}</td>
                        <td>
                          <div className="fw-bold text-dark font-14">{item.recipient_name}</div>
                          <small className="text-muted">{item.recipient_email}</small>
                        </td>
                        <td className="fw-semibold text-primary font-13">{item.subject}</td>
                        <td>
                          <Badge bg="purple" className="px-2.5 py-1 font-11 rounded-pill" style={{ background: "#8b5cf6" }}>
                            <BsStars className="me-1" /> AI Drafted
                          </Badge>
                        </td>
                        <td className="font-13 text-muted">
                          {item.sent_at ? HelperService.formatDate(item.sent_at) : "Today"}
                        </td>
                        <td className="text-center">
                          <Button
                            variant="outline-primary"
                            size="sm"
                            className="rounded-circle p-1.5"
                            title="View Mail Body"
                            onClick={() => {
                              setSelectedItem({ type: "MAIL", ...item });
                              setShowViewModal(true);
                            }}
                          >
                            <BsEye size={15} />
                          </Button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={6} className="text-center py-4 text-muted font-14">
                        No email logs found.
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </div>
          )}

          {activeTab === "CALLS" && (
            <div className="table-responsive">
              <Table hover className="align-middle mb-0 table-style-1">
                <thead className="bg-light">
                  <tr>
                    <th className="py-3 px-3">#</th>
                    <th className="py-3">Contact Person</th>
                    <th className="py-3">Duration</th>
                    <th className="py-3">Outcome</th>
                    <th className="py-3">Notes Summary</th>
                    <th className="py-3">Follow-up Date</th>
                    <th className="py-3 text-center">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {getFilteredCalls().length > 0 ? (
                    getFilteredCalls().map((item: any, idx: number) => (
                      <tr key={idx}>
                        <td className="px-3 font-13 fw-semibold">{idx + 1}</td>
                        <td>
                          <div className="fw-bold text-dark font-14">{item.contact_name}</div>
                          <small className="text-muted">{item.contact_email || "N/A"}</small>
                        </td>
                        <td className="font-13 fw-semibold">{item.duration}</td>
                        <td>
                          <Badge
                            bg={
                              item.outcome === "POSITIVE"
                                ? "success"
                                : item.outcome === "FOLLOWUP_NEEDED"
                                  ? "warning"
                                  : "secondary"
                            }
                            pill
                            className="px-2.5 py-1 font-11"
                          >
                            {item.outcome}
                          </Badge>
                        </td>
                        <td className="font-13 text-muted" style={{ maxWidth: "300px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                          {item.notes}
                        </td>
                        <td className="font-13 text-muted">{item.followup_date || "N/A"}</td>
                        <td className="text-center">
                          <Button
                            variant="outline-success"
                            size="sm"
                            className="rounded-circle p-1.5"
                            title="View Discussion Notes"
                            onClick={() => {
                              setSelectedItem({ type: "CALL", ...item });
                              setShowViewModal(true);
                            }}
                          >
                            <BsEye size={15} />
                          </Button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={7} className="text-center py-4 text-muted font-14">
                        No call logs found.
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </div>
          )}
        </Card>
      </Container>

      {/* Log Details Modal */}
      <Modal show={showViewModal} onHide={() => setShowViewModal(false)} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title className="font-18 fw-bold">Log Record Details</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedItem && (
            <div>
              {selectedItem.type === "MAIL" && (
                <>
                  <p><strong>To:</strong> {selectedItem.recipient_name} ({selectedItem.recipient_email})</p>
                  <p><strong>Subject:</strong> {selectedItem.subject}</p>
                  <hr />
                  <h6>Email Content:</h6>
                  <div className="bg-light p-3 rounded font-14" style={{ whiteSpace: "pre-wrap" }}>
                    {selectedItem.content}
                  </div>
                </>
              )}

              {selectedItem.type === "CALL" && (
                <>
                  <p><strong>Contact:</strong> {selectedItem.contact_name}</p>
                  <p><strong>Duration:</strong> {selectedItem.duration} | <strong>Outcome:</strong> {selectedItem.outcome}</p>
                  <p><strong>Next Followup Date:</strong> {selectedItem.followup_date || "None"}</p>
                  <hr />
                  <h6>Call Summary & Notes:</h6>
                  <div className="bg-light p-3 rounded font-14" style={{ whiteSpace: "pre-wrap" }}>
                    {selectedItem.notes}
                  </div>
                </>
              )}
            </div>
          )}
        </Modal.Body>
      </Modal>

      {/* Action Modals */}
      <RmAiEmailModal
        show={showEmailModal}
        onHide={() => setShowEmailModal(false)}
      />

      <RmLogActivityModal
        show={showLogModal}
        onHide={() => setShowLogModal(false)}
      />
    </div>
  );
};

export default RmInteractionLogs;
